import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  getDoc,
  doc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../firebaseConfig";
import "./MyNetworkPage.css";
import PostCard from "./PostCard";
import { isPostExpired } from "../utils/postLifecycle";

const MyNetworkPage = () => {
  const [following, setFollowing] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPosts, setUserPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPortraitMobile, setIsPortraitMobile] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const navigate = useNavigate(); 

  // ✅ Load following list for current user
  useEffect(() => {
    const fetchFollowing = async () => {
      const user = auth.currentUser;
      if (!user) return;

      const ref = doc(db, "users", user.uid);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        setFollowing(data.following || []);
        if (data.following && data.following.length > 0) {
          setSelectedUser(data.following[0]); // show first followed user by default
        }
      }
      setLoading(false);
    };

    fetchFollowing();
  }, []);

  // ✅ Listen for posts from selected user
  useEffect(() => {
    const timerId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 60 * 1000);

    return () => window.clearInterval(timerId);
  }, []);

  useEffect(() => {
    if (!selectedUser) return;

    const q = query(
      collection(db, "posts"),
      where("userId", "==", selectedUser.userId),
      orderBy("createdAt", "desc"),
      limit(60)
    );

    const unsub = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter(
          (post) =>
            post.privacyTier === "real" ||
            post.privacyTier === "usernameDept"
        );
      setUserPosts(list);
    });

    return () => unsub();
  }, [selectedUser]);

  const visibleUserPosts = useMemo(
    () => userPosts.filter((post) => !isPostExpired(post, nowMs)).slice(0, 20),
    [userPosts, nowMs]
  );

  // Detect portrait mobile (<=480px width)
  useEffect(() => {
    const check = () => {
      const isPortrait = window.matchMedia && window.matchMedia('(orientation: portrait)').matches;
      setIsPortraitMobile(window.innerWidth <= 480 && isPortrait);
    };
    check();
    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', check);
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', check);
    };
  }, []);

  if (loading) {
    return (
      <div className="mynetwork-page">
      {/* Mobile portrait: dropdown toggle for followed users */}
      {isPortraitMobile && (
        <div className="network-dropdown-bar">
          <button
            className="network-dropdown-toggle"
            onClick={() => setIsDropdownOpen(true)}
          >
            {selectedUser?.userName || (following[0]?.userName || 'Following')}
            <span className="caret">▼</span>
          </button>
        </div>
      )}

      {/* Overlay dropdown list (opens over content) */}
      {isPortraitMobile && isDropdownOpen && (
        <div
          className="network-dropdown-overlay"
          onClick={() => setIsDropdownOpen(false)}
        >
          <div
            className="network-dropdown-panel"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="network-dropdown-close"
              onClick={() => setIsDropdownOpen(false)}
              aria-label="Close"
            >
              ×
            </button>
            <ul className="network-dropdown-list">
              {following.length === 0 ? (
                <li className="network-dropdown-empty">No followed users</li>
              ) : (
                following.map((u) => (
                  <li
                    key={u.userId}
                    className={`network-dropdown-item ${selectedUser?.userId === u.userId ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedUser(u);
                      setIsDropdownOpen(false);
                    }}
                  >
                    <span className="network-dropdown-name">{u.userName}</span>
                    {u.agency ? (
                      <span className="network-dropdown-agency">{u.agency}</span>
                    ) : null}
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      )}

      <div className="mynetwork-container">
        <p>Loading your network...</p>
      </div>
      </div>
    );
  }



  return (
    <div className="mynetwork-page">
      <div className="network-header-top">
        <h1>My Network</h1>
      </div>
      

      {/* Mobile portrait: dropdown toggle for followed users */}
      {isPortraitMobile && (
        <div className="network-dropdown-bar">
 <div className="wire-tabs-dropdown">
  <button
    className={`wire-tab-dropdown ${window.location.pathname === "/thewire" ? "active" : ""}`}
    onClick={() => navigate("/thewire")}
  >
    📰 ←The Wire
  </button>
  </div>
          <button
            className="network-dropdown-toggle"
            onClick={() => setIsDropdownOpen(true)}
          >
            {selectedUser?.userName || (following[0]?.userName || 'Following')}
            <span className="caret">▼</span>
          </button>
        </div>
      )}

      {/* Overlay dropdown list (opens over content) */}
      {isPortraitMobile && isDropdownOpen && (
        <div
          className="network-dropdown-overlay"
          onClick={() => setIsDropdownOpen(false)}
        >
          <div
            className="network-dropdown-panel"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="network-dropdown-close"
              onClick={() => setIsDropdownOpen(false)}
              aria-label="Close"
            >
              ×
            </button>
            <ul className="network-dropdown-list">
              {following.length === 0 ? (
                <li className="network-dropdown-empty">No followed users</li>
              ) : (
                following.map((u) => (
                  <li
                    key={u.userId}
                    className={`network-dropdown-item ${selectedUser?.userId === u.userId ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedUser(u);
                      setIsDropdownOpen(false);
                    }}
                  >
                    <span className="network-dropdown-name">{u.userName}</span>
                    {u.agency ? (
                      <span className="network-dropdown-agency">{u.agency}</span>
                    ) : null}
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      )}
      <div className="mynetwork-container">
      <div className="network-left">
           <div className="wire-tabs">
  <button
    className={`wire-tab ${window.location.pathname === "/thewire" ? "active" : ""}`}
    onClick={() => navigate("/thewire")}
  >
    📰 ←The Wire
  </button>
  </div>

        {following.length === 0 ? (
          <p className="no-following">You aren’t following anyone yet.</p>
        ) : (
          <ul className="network-list">
            {following.map((u) => (
              <li
                key={u.userId}
                className={`network-item ${
                  selectedUser?.userId === u.userId ? "active" : ""
                }`}
                onClick={() => setSelectedUser(u)}
              >
                <span className="network-name">{u.userName}</span>
                <span className="network-agency">{u.agency}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="network-right">
        {selectedUser ? (
          <>
            <h3 className="network-header">
              {selectedUser.userName} — {selectedUser.agency}
            </h3>
            {visibleUserPosts.length === 0 ? (
              <p className="no-posts">No posts yet from this user.</p>
            ) : (
              visibleUserPosts.map((post) => <PostCard key={post.id} post={post} />)
            )}
          </>
        ) : (
          <p className="no-selection">Select a user to view their posts.</p>
        )}
      </div>
    </div>
    </div>
  );
};

export default MyNetworkPage;

